import dayjs from 'dayjs';

import type { AppointmentSlot } from '@/common/api/models/appointmentSlot';
import type { Booking } from '@/common/api/models/booking';
import { SlotStatus } from '@/common/api/models/slotStatus';
import { VideoMeetingProvider } from '@/common/api/models/videoMeetingProvider';
import type { StudentPreviewBookingState } from '@/common/studentPreview/StudentPreviewSource';
import type { Module, Task } from '@/student/types';

export const ORAL_EXAM_TASK_LAB_STATES = [
  'locked',
  'can-book',
  'no-slots',
  'booked',
  'booked-soon',
  'lobby-open',
  'in-progress',
  'missed',
  'cancelled',
  'taken',
  'failed-rebook',
] as const;

export type OralExamTaskLabState = typeof ORAL_EXAM_TASK_LAB_STATES[number];

export const ORAL_EXAM_TASK_LAB_STATE_LABELS: Record<OralExamTaskLabState, string> = {
  locked: 'Locked',
  'can-book': 'Can book',
  'no-slots': 'No slots',
  booked: 'Booked',
  'booked-soon': 'Booked, today',
  'lobby-open': 'Lobby open',
  'in-progress': 'Exam running, join closed',
  missed: 'Missed',
  cancelled: 'Booking cancelled',
  taken: 'Exam taken',
  'failed-rebook': 'Failed, book again',
};

export const ORAL_EXAM_TASK_LAB_STATE_NOTES: Record<OralExamTaskLabState, string> = {
  locked: 'An earlier task is not done yet, so registration is blocked.',
  'can-book': 'Open slots, no booking yet.',
  'no-slots': 'No booking and no open slots.',
  booked: 'Confirmed for in five days; can still cancel (more than 24 hours ahead).',
  'booked-soon': 'Confirmed for in three hours; too late to cancel, lobby not open yet.',
  'lobby-open': 'Starts in ten minutes; the lobby is open.',
  'in-progress': 'Started ten minutes ago and the student is not in; the join window has closed.',
  missed: 'Ended two hours ago without the student.',
  cancelled: 'The booking was cancelled; open slots are offered again.',
  taken: 'Attended two days ago.',
  'failed-rebook': 'A previous attempt counted as failed; open slots are offered again.',
};

export const ORAL_EXAM_TASK_LAB_IDS = {
  courseId: 'lab-course',
  moduleId: 'lab-module',
  taskId: 'lab-oral-exam',
};

const LAB_QUESTIONS = [
  'Stakeholder Incentives: Incentives shaping launch requirements.\n- Give a clear definition of stakeholder incentives in an AI launch context.\n- Trace a cause/effect chain showing how incentives translate into launch requirements and trade-offs.\n- Develop a concrete example from an organization you know.',
  'Defensible Value: Value grounded in the job to be done.\n- Characterize what defensible value means for an AI application at launch.\n- Explain the mechanism by which solving a precise job strengthens advantage.\n- Walk through a stepwise implementation in a context you choose.',
  'Value and Trust Metrics: Choosing signals that lead.\n- Describe what makes a metric a leading signal for an early AI launch.\n- Diagnose what goes wrong when teams rely on lagging measures.\n- Find a revealing example from a product context.',
  'Governance Failures: From assurance to incident.\n- Describe what a governance assurance promises in an AI product.\n- Explain how a breach moves through the lifecycle.\n- Contrast how this plays out across two sectors.',
  'AI Dataflow Design: From input to feedback.\n- Describe what an end-to-end dataflow is in an AI application.\n- Break it into its parts and explain how they work together.\n- Design a dataflow for a scenario you choose.',
  'Storage and Retrieval Strategy: Supplying the right signals at inference.\n- Describe the purpose of storing and retrieving signals for model use.\n- Explain how storage choices affect latency, cost, and risk.\n- Pick a scenario and explain how you would design storage and access.',
  'Explainability in UX: Communicating an AI decision.\n- Describe what explainability means when a user receives an AI suggestion.\n- Break it into parts and explain how they build calibrated trust.\n- Develop an example of a decision moment.',
  'Launch Readiness: Deciding when to ship.\n- Describe what launch readiness means for an AI feature.\n- Explain which risks block a launch and which can be monitored.\n- Apply this to a launch you know.',
].map((question, index) => ({ questionId: `lab-q${index + 1}`, question }));

const at = (offset: dayjs.Dayjs) => offset.second(0).millisecond(0);

function slot(slotId: string, start: dayjs.Dayjs, minutes = 60): AppointmentSlot {
  return {
    slotId,
    startTime: at(start).toISOString(),
    endTime: at(start.add(minutes, 'minute')).toISOString(),
    capacity: 1,
    availableSpots: 1,
    durationMinutes: minutes,
    status: SlotStatus.available,
    lecturerId: 'lab-lecturer',
    eventKindId: 'oral_exam_20',
    eventKindLabel: 'Oral exam',
    boundTasks: [],
    videoMeetingProvider: VideoMeetingProvider.livekit,
  } as AppointmentSlot;
}

function booking(
  bookedSlot: AppointmentSlot,
  status: Booking['status'],
  overrides: Partial<Booking> = {},
): Booking {
  return {
    bookingId: `lab-booking-${bookedSlot.slotId}`,
    slotId: bookedSlot.slotId,
    taskId: ORAL_EXAM_TASK_LAB_IDS.taskId,
    moduleId: ORAL_EXAM_TASK_LAB_IDS.moduleId,
    courseId: ORAL_EXAM_TASK_LAB_IDS.courseId,
    status,
    startTime: bookedSlot.startTime,
    endTime: bookedSlot.endTime,
    videoMeetingProvider: VideoMeetingProvider.livekit,
    oralExamAdmitted: false,
    ...overrides,
  } as Booking;
}

function openSlots(now: dayjs.Dayjs): AppointmentSlot[] {
  return [
    slot('lab-open-1', now.add(2, 'day').hour(10).minute(0)),
    slot('lab-open-2', now.add(5, 'day').hour(14).minute(0)),
    slot('lab-open-3', now.add(8, 'day').hour(9).minute(0)),
  ];
}

export type OralExamTaskLabFixture = {
  task: Task;
  module: Module;
  bookingState: StudentPreviewBookingState;
};

/** One consistent task, module and booking state, built relative to now. */
export function buildOralExamTaskLabFixture(state: OralExamTaskLabState): OralExamTaskLabFixture {
  const now = dayjs();
  let slots = openSlots(now);
  let bookings: Booking[] = [];
  let registeredExamAt: string | null = null;
  let latestFailureReason: string | undefined;

  const pin = (bookedSlot: AppointmentSlot, status: Booking['status'], overrides: Partial<Booking> = {}) => {
    slots = [bookedSlot, ...slots];
    bookings = [booking(bookedSlot, status, overrides)];
    registeredExamAt = bookedSlot.startTime;
  };

  switch (state) {
    case 'no-slots':
      slots = [];
      break;
    case 'booked':
      pin(slot('lab-booked', now.add(5, 'day').hour(10).minute(0)), 'confirmed', { oralExamJoinState: 'upcoming' });
      break;
    case 'booked-soon':
      pin(slot('lab-booked', now.add(3, 'hour').minute(0)), 'confirmed', { oralExamJoinState: 'upcoming' });
      break;
    case 'lobby-open':
      pin(slot('lab-booked', now.add(10, 'minute')), 'confirmed', { oralExamJoinState: 'open' });
      break;
    case 'in-progress':
      pin(slot('lab-booked', now.subtract(10, 'minute')), 'confirmed', { oralExamJoinState: 'in_progress' });
      break;
    case 'missed':
      pin(slot('lab-booked', now.subtract(2, 'hour').minute(0)), 'confirmed', { oralExamJoinState: 'over' });
      break;
    case 'cancelled':
      bookings = [booking(slot('lab-cancelled', now.add(5, 'day').hour(10).minute(0)), 'cancelled')];
      break;
    case 'taken':
      pin(slot('lab-attended', now.subtract(2, 'day').hour(9).minute(0)), 'completed');
      slots = [];
      break;
    case 'failed-rebook':
      bookings = [booking(slot('lab-attended', now.subtract(30, 'day').hour(9).minute(0)), 'completed')];
      latestFailureReason = 'you did not complete check-in before the exam started.';
      break;
    default:
      break;
  }

  const task = {
    id: ORAL_EXAM_TASK_LAB_IDS.taskId,
    type: 'oral_exam',
    title: 'Oral Exam',
    description: '',
    eventConfig: { eventKindId: 'oral_exam_20', locked: state === 'locked' },
    oralExamConfig: {
      questionPool: LAB_QUESTIONS,
      resolvedQuestionCount: 2,
      resolvedSecondsPerQuestion: 150,
      questionPoolLock: registeredExamAt ? 'registered' : 'open',
      registeredExamAt,
    },
    submission: { state: 'draft' },
  } as unknown as Task;

  const module = {
    id: ORAL_EXAM_TASK_LAB_IDS.moduleId,
    title: 'Plan, build, launch LLM-backed AI app',
    version: '1',
    courses: [{ id: ORAL_EXAM_TASK_LAB_IDS.courseId }],
    examAttempt: latestFailureReason ? { latestFailureReason } : undefined,
  } as unknown as Module;

  return { task, module, bookingState: { slots, bookings, examSessionStatus: null } };
}

export function parseOralExamTaskLabState(value: string | null): OralExamTaskLabState {
  return (ORAL_EXAM_TASK_LAB_STATES as readonly string[]).includes(value ?? '')
    ? value as OralExamTaskLabState
    : 'can-book';
}
